'use client'
import styled from '@emotion/styled'
import { useForm } from 'react-hook-form'
import { ErrorText, Input } from '@common/components/Input'
import React, { useEffect, useState } from 'react'
import { ButtonContainer } from '@common/components/ButtonContainer'
import { Button } from '@common/components/Button'
import { useSignIn } from '@common/hooks/use-sign-in'
import { useToken } from '@common/hooks/use-token'
import { useSlug } from '@common/hooks/use-slug'
import { EmailOptions, PhoneNumberOptions } from '@common/utils/forms'
import { PhoneInput } from '@common/components/PhoneInput'
import { useStaySignedIn } from '@common/hooks/use-stay-signed-in'
import { ResponsivePadding } from '@common/features/blocks/common/ResponsivePadding'
import { useSlimStaticWebsite } from '@app/site/common/hooks/use-slim-static-website'
import { useRouter, useSearchParams } from 'next/navigation'
import { hardReload } from '@common/utils/hardReload'

export default function ClientPage() {
  const { storeCredentials } = useStaySignedIn()
  const { sendSignInCode, loading } = useSignIn()
  const query = useSearchParams()

  const [phoneNumber, setPhoneNumber] = useState(query.get('p') ?? '')
  const [remainingTime, setRemainingTime] = useState(0)
  const [email, setEmail] = useState(query.get('e') ?? '')
  const [tab, setTab] = useState<Screens>('phone')
  const [error, setError] = useState('')

  const slug = useSlug()
  const router = useRouter()
  const { name, loading: loadingWebsite } = useSlimStaticWebsite(slug)
  const { setToken } = useToken()

  useEffect(() => {
    setError('')
  }, [email, phoneNumber, tab])

  const isValid = !!name && !loadingWebsite

  return (
    <>
      <Container>
        {tab === 'phone' && isValid && (
          <PhoneNumberPrompt
            loading={loading}
            email={email}
            phoneNumber={phoneNumber}
            onNext={(phoneNumber, email) => {
              sendSignInCode(phoneNumber, email).then(({ data }) => {
                if (data?.signIn?.error) {
                  setError(data.signIn.error)
                  return
                }

                if (!data?.signIn?.retryInSeconds) {
                  setError('Ett oväntat fel uppstod')
                  return
                }

                setEmail(email)
                setPhoneNumber(phoneNumber)
                setRemainingTime(data.signIn.retryInSeconds)
                setTab('code')
              })
            }}
          />
        )}
        {tab === 'code' && isValid && (
          <CodePrompt
            phoneNumber={phoneNumber}
            email={email}
            defaultRemainingTime={remainingTime}
            onSuccess={(accessToken) => {
              setToken(accessToken)
              storeCredentials(email, phoneNumber)

              const redirectTo = query.get('to') ?? ''

              router.replace('/' + redirectTo)
              hardReload('/' + redirectTo)
            }}
            onBack={() => setTab('phone')}
          />
        )}

        {error && isValid && <ErrorText error={error} style={{ justifyContent: 'center' }} />}
      </Container>
    </>
  )
}

const Form = styled.form`
  width: 100%;

  display: flex;
  justify-content: center;

  margin-top: 15vh;

  @media (max-width: 600px) {
    width: 100%;
  }
`

const InputContainer = styled.div`
  @media (max-width: 600px) {
    width: 100%;
  }
`

const Subtitle = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.onBackgroundSemiLight};
`

const Header = styled.div`
  color: ${({ theme }) => theme.onBackground};
  font-weight: 600;

  font-size: 2rem;

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`

type Screens = 'phone' | 'code'

const PhoneNumberPrompt: React.FC<{
  phoneNumber: string
  email: string
  onNext: (phoneNumber: string, email: string) => void
  loading?: boolean
}> = ({ phoneNumber, email, onNext, loading }) => {
  const { storedEmail, storedPhoneNumber } = useStaySignedIn()
  const router = useRouter()
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ phoneNumber: string; email: string }>({
    defaultValues: { phoneNumber, email },
  })

  useEffect(() => {
    if (storedPhoneNumber) {
      setValue('phoneNumber', storedPhoneNumber)
    }

    if (storedEmail) {
      setValue('email', storedEmail)
    }
  }, [storedPhoneNumber, storedEmail, setValue])

  const handleNext = () => {
    onNext(getValues('phoneNumber'), getValues('email'))
  }

  register('phoneNumber', PhoneNumberOptions)

  return (
    <Form onSubmit={handleSubmit(handleNext)}>
      <InputContainer>
        <Header style={{ marginBottom: '2rem' }}>Logga in på ditt konto</Header>
        <Input
          error={errors.email?.message}
          autoFocus={true}
          autoComplete="on"
          placeholder="mail@gmail.com"
          type="email"
          label="E-mail"
          {...register('email', EmailOptions)}
        />
        <div style={{ marginTop: '1rem' }} />
        <PhoneInput
          label="Telefonnummer"
          error={errors.phoneNumber?.message}
          value={watch('phoneNumber')}
          onChange={(e) => {
            setValue('phoneNumber', e.currentTarget.value)
          }}
        />
        <div style={{ marginTop: '1rem' }} />
        <Button loading={loading} style={{ width: '100%' }} type="submit">
          Logga in
        </Button>
        <Button
          variant="secondary"
          onClick={(e) => {
            e.preventDefault()
            router.back()
          }}
          style={{ width: '100%', paddingTop: '2rem' }}
        >
          Avbryt
        </Button>
      </InputContainer>
    </Form>
  )
}

const CodePrompt: React.FC<{
  phoneNumber: string
  email: string
  defaultRemainingTime: number
  onSuccess: (accessToken: string) => void
  onBack: () => void
}> = ({ phoneNumber, email, defaultRemainingTime, onSuccess, onBack }) => {
  const { sendSignInCode, verifySignInCode, loading } = useSignIn()
  const [remainingTime, setRemainingTime] = useState(defaultRemainingTime)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    const handle = setTimeout(() => {
      setRemainingTime((prev) => {
        if (prev - 1 <= 0) {
          clearInterval(handle)

          return 0
        }

        return prev - 1
      })
    }, 1000)

    return () => clearTimeout(handle)
  }, [remainingTime])

  useEffect(() => {
    if (code !== '') {
      setError('')
    }
    if (code.length >= 4) {
      verifySignInCode(phoneNumber, email, code, false).then(({ data }) => {
        if (data?.signInVerify?.accessToken) {
          onSuccess(data.signInVerify.accessToken)
          return
        }

        setError(data?.signInVerify?.error ?? '')
        setCode('')
      })
    }
  }, [code])

  const handleResendCode = () => {
    sendSignInCode(phoneNumber, email).then(({ data }) => {
      const retryInSeconds = data?.signIn?.retryInSeconds

      if (retryInSeconds) {
        setRemainingTime(retryInSeconds)
      }
    })
  }

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <InputContainer>
        <Header style={{ marginBottom: '0.25rem' }}>Ange engångskoden</Header>
        <Subtitle style={{ marginBottom: '1.5rem' }}>En 4-siffrig kod har skickats till {phoneNumber}</Subtitle>
        <Input
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
          autoFocus={true}
          error={error}
          label="Engångskod"
          placeholder="1234"
          autoComplete="one-time-code"
        />
        <div style={{ marginTop: '1rem' }} />
        <ButtonContainer>
          <Button onClick={() => handleResendCode()} disabled={remainingTime !== 0} loading={loading}>
            Skicka igen {remainingTime !== 0 ? `(${remainingTime})` : ''}
          </Button>
          <Button
            variant="secondary"
            onClick={(e) => {
              e.preventDefault()
              onBack()
            }}
          >
            Tillbaka
          </Button>
        </ButtonContainer>
      </InputContainer>
    </Form>
  )
}

const Container = styled.div`
  ${ResponsivePadding};
`
